import { Injectable } from '@angular/core';
import { UserInfo } from '@memberjunction/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, take, distinctUntilChanged } from 'rxjs/operators';
import { UserService } from './user.service';
import { ImpersonationService, ImpersonationSession } from './impersonation.service';
import { UserAuthService } from './user-auth.service';

/**
 * Active user information that components should use
 */
export interface ActiveUserInfo {
  /** The user whose context is currently active (could be impersonated user) */
  activeUser: UserInfo;
  /** The real authenticated user (always the logged-in user) */
  realUser: UserInfo;
  /** Whether currently in impersonation mode */
  isImpersonating: boolean;
  /** Impersonation session details if active */
  impersonationSession?: ImpersonationSession;
}

/**
 * Wrapper service around UserService that provides unified access to user context
 * Handles both normal user access and impersonation scenarios transparently
 * 
 * Components should use this service instead of UserService directly to support impersonation
 */
@Injectable({
  providedIn: 'root'
})
export class ActiveUserService {
  private activeUserSubject = new BehaviorSubject<ActiveUserInfo | null>(null);
  
  /**
   * Observable that emits the current active user context
   * Components should subscribe to this for reactive user updates
   */
  public activeUser$ = this.activeUserSubject.asObservable();

  /**
   * Observable that indicates when the active user context is fully loaded
   */
  public activeUserReady$: Observable<boolean>;

  private roleGuardInstance: any = null;

  constructor(
    private userService: UserService,
    private impersonationService: ImpersonationService,
    private userAuthService: UserAuthService
  ) {
    // Initialize activeUserReady$ based on when we have user data
    this.activeUserReady$ = this.activeUser$.pipe(
      map(activeUserInfo => activeUserInfo !== null)
    );

    // Initialize the service
    this.initializeActiveUser();
  }

  /**
   * Sets the RoleGuard instance for cache clearing
   * This is set by RoleGuard itself to avoid circular dependency
   */
  public setRoleGuardInstance(roleGuard: any): void {
    this.roleGuardInstance = roleGuard;
  }

  /**
   * Gets the current active user (may be impersonated user)
   * @returns Active user info or null if not loaded
   */
  public getActiveUser(): UserInfo | null {
    const activeUserInfo = this.activeUserSubject.value;
    return activeUserInfo?.activeUser || null;
  }

  /**
   * Gets the real authenticated user (always the logged-in user)
   * @returns Real user info or null if not loaded
   */
  public getRealUser(): UserInfo | null {
    const activeUserInfo = this.activeUserSubject.value;
    return activeUserInfo?.realUser || null;
  }

  /**
   * Gets complete active user information including impersonation status
   * @returns Complete active user info or null if not loaded
   */
  public getActiveUserInfo(): ActiveUserInfo | null {
    return this.activeUserSubject.value;
  }

  /**
   * Checks if currently in impersonation mode
   * @returns True if impersonating another user
   */
  public isImpersonating(): boolean {
    const activeUserInfo = this.activeUserSubject.value;
    return activeUserInfo?.isImpersonating || false;
  }

  /**
   * Gets the current impersonation session if active
   * @returns Impersonation session or null if not impersonating
   */
  public getImpersonationSession(): ImpersonationSession | null {
    const activeUserInfo = this.activeUserSubject.value;
    return activeUserInfo?.impersonationSession || null;
  }

  /**
   * Gets active user roles for permission checking
   * Uses role from UserAuthService which fetches from API
   * For impersonated users, fetches role using their email from MembershipRoster
   * @returns Array of role names (single role from API)
   */
  public getActiveUserRoles(): string[] {
    // For impersonated users, we need to fetch their role separately
    if (this.isImpersonating()) {
      // Return empty array for now - role will be fetched asynchronously
      // Components should use getActiveUserRolesAsync() for impersonated users
      return [];
    }

    // Get role from UserAuthService (API-based) for normal logged-in user
    const role = this.userAuthService.getUserRole();

    // Return role as array for compatibility with existing code
    // API returns single role, not multiple roles
    return role ? [role] : [];
  }

  /**
   * Gets active user roles asynchronously - handles both normal and impersonated users
   * Uses ExtendedUsers for faster role lookups
   * @returns Promise resolving to array of role names
   */
  public async getActiveUserRolesAsync(): Promise<string[]> {
    if (this.isImpersonating()) {
      const activeUser = this.getActiveUser();
      if (activeUser?.ID && activeUser?.Email) {
        // Use getRoleByUserId which checks ExtendedUsers first
        const role = await this.userAuthService.getRoleByUserId(activeUser.ID, activeUser.Email);
        return role ? [role] : [];
      }
      return [];
    }

    // For normal users, try to fetch from API if not already cached
    const cachedRole = this.userAuthService.getUserRole();
    if (cachedRole) {
      return [cachedRole];
    }

    // Fetch role from API
    try {
      const role = await this.userAuthService.fetchUserRole();
      return role ? [role] : [];
    } catch (error) {
      console.error('Error fetching user role:', error);
      return [];
    }
  }

  /**
   * Checks if active user has a specific role
   * @param roleName - Role name to check
   * @returns True if user has the role
   */
  public hasRole(roleName: string): boolean {
    const roles = this.getActiveUserRoles();
    return roles.includes(roleName);
  }

  /**
   * Checks if active user has any of the specified roles
   * @param roleNames - Array of role names to check
   * @returns True if user has at least one of the roles
   */
  public hasAnyRole(roleNames: string[]): boolean {
    const userRoles = this.getActiveUserRoles();
    return roleNames.some(roleName => userRoles.includes(roleName));
  }

  /**
   * Checks if active user has all of the specified roles
   * @param roleNames - Array of role names to check
   * @returns True if user has all the roles
   */
  public hasAllRoles(roleNames: string[]): boolean {
    const userRoles = this.getActiveUserRoles();
    return roleNames.every(roleName => userRoles.includes(roleName));
  }

  /**
   * Async version: Checks if active user has a specific role
   * @param roleName - Role name to check
   * @returns Promise resolving to true if user has the role
   */
  public async hasRoleAsync(roleName: string): Promise<boolean> {
    const roles = await this.getActiveUserRolesAsync();
    return roles.includes(roleName);
  }

  /**
   * Async version: Checks if active user has any of the specified roles
   * @param roleNames - Array of role names to check
   * @returns Promise resolving to true if user has at least one of the roles
   */
  public async hasAnyRoleAsync(roleNames: string[]): Promise<boolean> {
    const userRoles = await this.getActiveUserRolesAsync();
    return roleNames.some(roleName => userRoles.includes(roleName));
  }

  /**
   * Async version: Checks if active user has all of the specified roles
   * @param roleNames - Array of role names to check
   * @returns Promise resolving to true if user has all the roles
   */
  public async hasAllRolesAsync(roleNames: string[]): Promise<boolean> {
    const userRoles = await this.getActiveUserRolesAsync();
    return roleNames.every(roleName => userRoles.includes(roleName));
  }

  /**
   * Gets active user ID (may be impersonated user ID)
   * @returns User ID or null if not loaded
   */
  public getActiveUserId(): string | null {
    const activeUser = this.getActiveUser();
    return activeUser?.ID || null;
  }

  /**
   * Gets real user ID (always the authenticated user ID)
   * @returns Real user ID or null if not loaded
   */
  public getRealUserId(): string | null {
    const realUser = this.getRealUser();
    return realUser?.ID || null;
  }

  /**
   * Gets active user name for display
   * @returns User name or null if not loaded
   */
  public getActiveUserName(): string | null {
    const activeUser = this.getActiveUser();
    return activeUser?.Name || null;
  }

  /**
   * Gets display name that includes impersonation context
   * @returns Formatted name showing impersonation status
   */
  public getDisplayName(): string {
    const activeUserInfo = this.activeUserSubject.value;
    if (!activeUserInfo) {
      return 'Loading...';
    }

    if (activeUserInfo.isImpersonating) {
      return `${activeUserInfo.activeUser.Name} (impersonated by ${activeUserInfo.realUser.Name})`;
    }

    return activeUserInfo.activeUser.Name;
  }

  /**
   * Refreshes the active user context
   * Should be called after impersonation changes
   */
  public async refreshActiveUser(): Promise<void> {
    await this.updateActiveUserContext();
  }

  /**
   * Forces a reload of user data from the backend
   * Useful after user profile changes
   */
  public async reloadUserData(): Promise<void> {
    // Force UserService to reload
    const realUser = this.userService.getUserInfo();
    if (realUser) {
      // Trigger userInfo reload - this depends on UserService implementation
      // For now, we'll just refresh our context
      await this.updateActiveUserContext();
    }
  }

  /**
   * Clears the active user context
   * Used during logout or authentication changes
   */
  public clearActiveUser(): void {
    this.activeUserSubject.next(null);
  }

  /**
   * Updates active user context based on real user and impersonation status
   * Private method called during initialization and after impersonation changes
   */
  private async updateActiveUserContext(): Promise<void> {
    try {
      // Wait for real user to be available
      const realUser = this.userService.getUserInfo();
      if (!realUser) {
        // Wait for user service to load
        setTimeout(() => this.updateActiveUserContext(), 100);
        return;
      }

      // Check for active impersonation session
      const impersonationSession = this.impersonationService.getCurrentSession();
      
      let activeUser: UserInfo;
      let isImpersonating: boolean;

      if (impersonationSession) {
        // Get full user info with roles for impersonated user
        const impersonatedUserWithRoles = await this.impersonationService.getImpersonatedUserWithRoles();
        if (impersonatedUserWithRoles) {
          activeUser = impersonatedUserWithRoles;
          isImpersonating = true;

          // Fetch the impersonated user's role from ExtendedUsers (fast) or fallback to Role API
          if (impersonatedUserWithRoles.ID && impersonatedUserWithRoles.Email) {
            try {
              const impersonatedUserRole = await this.userAuthService.getRoleByUserId(
                impersonatedUserWithRoles.ID,
                impersonatedUserWithRoles.Email
              );
              console.log(`Fetched role for impersonated user ${impersonatedUserWithRoles.Name}: ${impersonatedUserRole}`);
            } catch (error) {
              console.error('Error fetching impersonated user role:', error);
            }
          }
        } else {
          // Fallback to real user if we can't get impersonated user data
          activeUser = realUser;
          isImpersonating = false;
        }
      } else {
        // No impersonation session, use real user
        activeUser = realUser;
        isImpersonating = false;
      }

      // Create active user info
      const activeUserInfo: ActiveUserInfo = {
        activeUser,
        realUser,
        isImpersonating,
        impersonationSession: impersonationSession || undefined
      };

      // Emit the updated context
      this.activeUserSubject.next(activeUserInfo);
    } catch (error) {
      console.error('Error updating active user context:', error);
      // On error, still try to provide basic user info
      const realUser = this.userService.getUserInfo();
      if (realUser) {
        const activeUserInfo: ActiveUserInfo = {
          activeUser: realUser,
          realUser,
          isImpersonating: false,
          impersonationSession: undefined
        };
        this.activeUserSubject.next(activeUserInfo);
      }
    }
  }

  /**
   * Initializes the active user service
   * Sets up subscriptions to monitor user and impersonation changes
   */
  private initializeActiveUser(): void {
    // Subscribe to UserService changes
    this.userService.getUserComplete$.subscribe(userReady => {
      if (userReady) {
        this.updateActiveUserContext();
      }
    });

    // Subscribe to impersonation session changes
    // Use distinctUntilChanged to only trigger when impersonation status actually changes
    // (ignore timeRemaining updates which happen every second)
    this.impersonationService.getSessionStatus().pipe(
      distinctUntilChanged((prev, curr) => {
        // Only trigger if impersonation status or impersonated user changes
        // Ignore changes to timeRemaining to prevent constant cache clearing
        return prev.isImpersonating === curr.isImpersonating &&
               prev.session?.impersonatedUser?.ID === curr.session?.impersonatedUser?.ID;
      })
    ).subscribe(sessionStatus => {
      // Clear role guard cache when impersonation status changes
      // This ensures fresh permission checks with the new user context
      this.clearRoleGuardCache();

      // Refresh active user context when impersonation status changes
      // Only update if user service is ready to avoid race conditions
      const realUser = this.userService.getUserInfo();
      if (realUser) {
        this.updateActiveUserContext();
      }
    });

    // Initial load
    this.updateActiveUserContext();
  }

  /**
   * Helper method for components to easily check if user is loaded
   * @returns Promise that resolves when active user is ready
   */
  public async waitForActiveUser(): Promise<ActiveUserInfo> {
    return new Promise((resolve, reject) => {
      let subscription: any;
      let timeoutId: any;
      
      subscription = this.activeUser$.subscribe(activeUserInfo => {
        if (activeUserInfo) {
          if (subscription) {
            subscription.unsubscribe();
          }
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          resolve(activeUserInfo);
        }
      });

      // Timeout after 10 seconds
      timeoutId = setTimeout(() => {
        if (subscription) {
          subscription.unsubscribe();
        }
        reject(new Error('Timeout waiting for active user'));
      }, 10000);
    });
  }

  /**
   * Creates an observable that combines active user with other data
   * Useful for components that need both user context and other reactive data
   * @param other$ - Other observable to combine with
   * @returns Combined observable
   */
  public combineWithActiveUser<T>(other$: Observable<T>): Observable<{ activeUser: ActiveUserInfo | null; data: T }> {
    return combineLatest([this.activeUser$, other$]).pipe(
      map(([activeUser, data]) => ({ activeUser, data }))
    );
  }

  /**
   * Gets user preferences with fallback to real user if impersonating
   * Some preferences (like theme, language) should come from real user
   * @param useRealUser - Whether to use real user's preferences instead of active user
   * @returns User preferences object
   */
  public getUserPreferences(useRealUser: boolean = false): any {
    const user = useRealUser ? this.getRealUser() : this.getActiveUser();
    // Return user preferences - this would depend on how preferences are stored
    // For now, return empty object as placeholder
    return {};
  }

  /**
   * Checks if the active user can access a specific resource
   * This would integrate with the permission system
   * @param resource - Resource identifier
   * @param action - Action to check (read, write, delete, etc.)
   * @returns True if access is allowed
   */
  public canAccess(resource: string, action: string = 'read'): boolean {
    // This would implement permission checking logic
    // For now, return true as placeholder
    return true;
  }

  /**
   * Clears the role guard permission cache
   * Called when user context changes (e.g., impersonation start/end)
   */
  private clearRoleGuardCache(): void {
    if (this.roleGuardInstance && typeof this.roleGuardInstance.onUserContextChanged === 'function') {
      this.roleGuardInstance.onUserContextChanged();
    }
  }
}